import i18n from 'i18next';

// Namespaces we plan to support as we split files by domain
export const availableNamespaces = [
  'common',
  'trading',
  'myPage',
  'review',
  'community',
  'auth',
  'errors'
] as const;
export type Namespace = (typeof availableNamespaces)[number];

// Dynamic import loader for namespace JSON per language
// This relies on bundler code-splitting. If a namespace file isn't present,
// it fails gracefully and returns false.
export function normalizeLocale(l?: string | null): string {
  if (!l || typeof l !== 'string') return 'en';

  const raw = l.trim().replace(/^"(.*)"$/, '$1');
  const lower = raw.replace(/_/g, '-').toLowerCase();

  if (!lower) return 'en';
  if (lower.startsWith('en')) return 'en';
  if (lower.startsWith('ko')) return 'ko';
  if (lower.startsWith('ja') || lower.startsWith('jp')) return 'jp';
  if (lower.startsWith('es')) return 'es';
  if (lower.startsWith('pt')) return 'pt';
  if (lower.startsWith('vi')) return 'vi';
  if (lower.startsWith('th')) return 'th';
  if (lower.startsWith('fil') || lower.startsWith('tl')) return 'fil';
  if (lower === 'id' || lower.startsWith('id-') || lower.startsWith('ind')) return 'id';
  if (lower === 'zhtw' || lower === 'zh-tw' || lower === 'zh-hk' || lower === 'zh-mo') {
    return 'zh-TW';
  }
  if (lower === 'zhcn' || lower === 'zh-cn') return 'zh-CN';
  if (lower.startsWith('zh')) {
    if (
      lower.includes('hant') ||
      lower.includes('tw') ||
      lower.includes('hk') ||
      lower.includes('mo')
    ) {
      return 'zh-TW';
    }
    return 'zh-CN';
  }

  return 'en';
}

export function normalizeLang(l?: string | null): string {
  const locale = normalizeLocale(l);
  if (locale === 'zh-TW') return 'zhTW';
  if (locale === 'zh-CN') return 'zhCN';
  return locale;
}

export async function loadNamespace(ns: Namespace, lng?: string): Promise<boolean> {
  const language = normalizeLang(lng || i18n.language || 'en');

  try {
    // Webpack/Vite dynamic import pattern. Adjust path if you relocate JSONs.
    const module = await import(
      /* webpackChunkName: "i18n-[request]" */ `./locales/${language}/${ns}.json`
    );

    const resources = module.default || module;
    if (!i18n.hasResourceBundle(language, ns)) {
      i18n.addResourceBundle(language, ns, resources, true, true);
    }
    return true;
  } catch (_e) {
    // Namespace file not found for language; ignore for now
    return false;
  }
}

export async function ensureNamespaces(namespaces: Namespace[], lng?: string): Promise<void> {
  await Promise.all(namespaces.map((ns) => loadNamespace(ns, lng)));
}
